import { createApiRef } from '@backstage/core-plugin-api';
import { AxiosInstance } from 'axios';
import { AxiosInstanceProviderApi } from './axiosInstanceApi';

export type AssistantApprovalStatus = 'Approved' | 'Under Review' | string;

export interface AssistantOwner {
  name: string;
  email?: string;
  type?: string;
}

export interface AssistantReleaseNote {
  version: string;
  releasedate?: string;
  summary?: string;
  changes?: string[];
}

export interface AssistantRecord {
  id: string;
  name: string;
  description: string;
  segment?: string;
  lob?: string;
  aiCategory?: string;
  useCases?: string[];
  assistantType?: string;
  overview?: string;
  avatar?: {
    init?: string;
    color?: string;
  };
  owner?: AssistantOwner;
  targetAudience?: {
    designedfor?: string;
    businessgroup?: string;
    geography?: string;
    specificlocation?: string;
  };
  governance?: {
    dataaccess?: string;
    compliance?: string;
    validationsummary?: string;
    version?: string;
    validatedby?: string;
    importanttoknow?: string;
  };
  evidence?: {
    link?: string;
    filepath?: string;
    filename?: string;
    filetype?: string;
    filesize?: number;
  };
  capabilities?: string[];
  whatYouProvide?: string[];
  whatYouGet?: string[];
  integrationMethod?: {
    method?: string;
    configuration?: string;
  };
  agentConfiguration?: {
    agentUrl?: string;
    agentVersion?: string;
    skills?: string[];
    systemCapabilities?: string[];
  };
  interactionSettings?: {
    inputFormat?: string;
    responseFormat?: string;
    requiresSecureAccess?: boolean;
  };
  approvalStatus?: AssistantApprovalStatus;
  status?: AssistantApprovalStatus;
  comments?: string;
  updatedAt?: string;
  createdAt?: string;
  updatedat?: string;
  createdat?: string;
  createdBy?: string;
  createdby?: string;
  bookmarked?: boolean;
  approval?: {
    date?: string;
    approvedby?: string;
  };
  releaseNotes?: AssistantReleaseNote[];
}

export interface UpsertAssistantPayload {
  name: string;
  description: string;
  segment?: string;
  lob?: string;
  aiCategory?: string;
  useCases?: string[];
  assistantType?: string;
  overview?: string;
  avatar?: {
    init?: string;
    color?: string;
  };
  owner?: AssistantOwner;
  targetAudience?: {
    designedfor?: string;
    businessgroup?: string;
    geography?: string;
    specificlocation?: string;
  };
  governance?: {
    dataaccess?: string;
    compliance?: string;
    validationsummary?: string;
    version?: string;
    validatedby?: string;
    importanttoknow?: string;
  };
  evidence?: {
    link?: string;
    filepath?: string;
    filename?: string;
    filetype?: string;
    filesize?: number;
  };
  capabilities?: string[];
  whatYouProvide?: string[];
  whatYouGet?: string[];
  integrationMethod?: {
    method?: string;
    configuration?: string;
  };
  agentConfiguration?: {
    agentUrl?: string;
    agentVersion?: string;
    skills?: string[];
    systemCapabilities?: string[];
  };
  interactionSettings?: {
    inputFormat?: string;
    responseFormat?: string;
    requiresSecureAccess?: boolean;
  };
  approvalStatus?: AssistantApprovalStatus;
}

export interface ApprovalPayload {
  status: AssistantApprovalStatus;
  comments?: string;
}

export interface ExternalAgentPayload {
  provider: string;
  title: string;
  description: string;
  user_email: string;
  schema_id: string;
  environment_id: string;
  connection_string_url: string;
}

export interface AiAssistantsCatalogApi {
  createAssistant: (payload: UpsertAssistantPayload) => Promise<any>;
  updateAssistant: (assistantId: string, payload: UpsertAssistantPayload) => Promise<any>;
  updateApprovalStatus: (assistantId: string, payload: ApprovalPayload) => Promise<any>;
  getAllAssistants: () => Promise<any>;
  getUserBookmarks: () => Promise<any>;
  getAssistantById: (assistantId: string) => Promise<any>;
  bookmarkAssistant: (assistantId: string) => Promise<any>;
  removeBookmark: (assistantId: string) => Promise<any>;
  addReleaseNotes: (assistantId: string, payload: AssistantReleaseNote) => Promise<any>;
  getReleaseNotes: (assistantId: string) => Promise<any>;
  removeReleaseNotes: (assistantId: string, version: string) => Promise<any>;
  removeAssistant: (assistantId: string) => Promise<any>;
  createExternalAgent: (payload: ExternalAgentPayload) => Promise<any>;
}

export const aiAssistantsCatalogApiRef = createApiRef<AiAssistantsCatalogApi>({
  id: 'cbre.devx.api.ai-assistants-catalog',
});

export class AiAssistantsCatalogApiImpl implements AiAssistantsCatalogApi {
  private readonly axiosInstancePromise: Promise<AxiosInstance>;

  constructor(axiosInstanceProviderApi: AxiosInstanceProviderApi) {
    this.axiosInstancePromise = axiosInstanceProviderApi.getInstance();
  }

  async createAssistant(payload: UpsertAssistantPayload): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance.post('service/assistants', payload).then(res => res.data);
  }

  async updateAssistant(
    assistantId: string,
    payload: UpsertAssistantPayload,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .put(`service/assistants/${assistantId}`, payload)
      .then(res => res.data);
  }

  async updateApprovalStatus(
    assistantId: string,
    payload: ApprovalPayload,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .patch(`service/assistants/${assistantId}/approval`, payload)
      .then(res => res.data);
  }

  async getAllAssistants(): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance.get('service/assistants').then(res => res.data);
  }

  async getUserBookmarks(): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance.get('service/assistants/user/bookmarks').then(res => res.data);
  }

  async getAssistantById(assistantId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance.get(`service/assistants/${assistantId}`).then(res => res.data);
  }

  async bookmarkAssistant(assistantId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .post(`service/assistants/${assistantId}/bookmark`)
      .then(res => res.data);
  }

  async removeBookmark(assistantId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .delete(`service/assistants/${assistantId}/bookmark`)
      .then(res => res.data);
  }

  async addReleaseNotes(
    assistantId: string,
    payload: AssistantReleaseNote,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .post(`service/assistants/${assistantId}/releasenotes`, payload)
      .then(res => res.data);
  }

  async getReleaseNotes(assistantId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`service/assistants/${assistantId}/releasenotes`)
      .then(res => res.data);
  }

  async removeReleaseNotes(assistantId: string, version: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .delete(`service/assistants/${assistantId}/releasenotes/${version}`)
      .then(res => res.data);
  }

  async removeAssistant(assistantId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance.delete(`service/assistants/${assistantId}`).then(res => res.data);
  }

  async createExternalAgent(payload: ExternalAgentPayload): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .post('service/assistants/external-agents', payload)
      .then(res => res.data);
  }
}